<template>
  <div class="p-grid p-mt-4 p-mb-4" v-if="user?.employee">
    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4 p-fluid info-card">
      <Panel :header="$t('General information')">
        <div class="panel-inner">
          <div class="panel-info-block-grid">
            <div class="panel-info-block-label p-p-1">{{ $t('personName') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <div class="p-d-flex p-ai-center p-flex-wrap">
                <div v-if="user.first_name || user.last_name">{{ user.first_name || '' }} {{ user.last_name || '' }}</div>
                <i v-else class="ti-minus"/>
                <UserLanguageBadge :language="user.language"/>
              </div>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Position') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.employee?.position">{{ user.employee.position }}</span>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Personal code') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.employee?.personal_code">{{ user.employee.personal_code }}</span>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Phone') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <template v-if="user.phone_code || user.phone_number">
                <span v-if="user.phone_code && user.phone_number">+</span>{{ user.phone_code || '' }} {{ user.phone_number || '' }}
              </template>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Work Phone') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <template v-if="user.employee?.work_phone_code || user.employee?.work_phone_number">
                <span v-if="user.employee?.work_phone_code">+</span>{{ user.employee?.work_phone_code || '' }} {{ user.employee?.work_phone_number || '' }}
              </template>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Email') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <template v-if="user.type === constants.userTypes.legal_entity && user.company_name">
                <span v-if="user.company_email">{{ user.company_email }}</span>
                <i v-else class="ti-minus"/>
              </template>
              <template v-else>
                <span v-if="user.email">{{ user.email }}</span>
                <i v-else class="ti-minus"/>
              </template>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Birthdate') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.birthdate">{{ formatBirthDate(user.birthdate) }}</span>
              <i v-else class="ti-minus"/>
            </div>


            <div class="panel-info-block-label p-p-1">{{ $t('Created') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <template v-if="user.employee?.created_at">
                {{ formatBy(user.employee.created_at, user.employee?.creator) }}
              </template>
              <i v-else class="ti-minus"/>
            </div>
          </div>
        </div>
      </Panel>
    </div>

    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4 p-fluid info-card">
      <Panel :header="$t('additionalDetailsPanelTitle')">
        <div class="panel-inner">
          <div class="panel-info-block-grid">

            <div class="panel-info-block-label p-p-1">{{ $t('Address') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <template v-if="user.address">
                <span class="p-mr-1" v-show="user.address.address">{{ user.address.address }},</span>
                <span class="p-mr-1" v-show="user.address.address2">{{ user.address.address2 }},</span>
                <span class="p-mr-1" v-show="user.address.city">{{ user.address.city }},</span>
                <span class="p-mr-1" v-show="user.address.state">{{ user.address.state }},</span>
                <span class="p-mr-1" v-show="user.address.postcode">{{ user.address.postcode }},</span>
                <span class="p-mr-1" v-if="user.address.country">{{ user.address.country.name }} </span>
              </template>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Contract') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.employee?.contract">{{ user.employee.contract }}</span>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Start') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.employee?.start">{{ formatDay(user.employee.start) }}</span>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('End') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.employee?.end">{{ formatDay(user.employee.end) }}</span>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Last Login') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.last_login">{{ formatDate(user.last_login) }}</span>
              <i v-else class="ti-minus"/>
            </div>

            <div class="panel-info-block-label p-p-1">{{ $t('Comment') }}:</div>
            <div class="panel-info-block-value p-p-1">
              <span v-if="user.comment">{{ user.comment }}</span>
              <i v-else class="ti-minus"/>
            </div>
          </div>
        </div>
      </Panel>
    </div>

    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4 p-fluid" style="min-width: 234px">
      <FileUploader :item="user"
                    @updated="onAvatarUpdated"
                    :disabled="employeeIsLoading || billingDataEditingLocked"
                    :wrapperClass="'p-fieldset p-p-3'"
                    field="profile_img"
                    endpoint="user/set-avatar"
                    delete-endpoint="user/delete-avatar"
                    :currentUrl="avatarCurrentUrl"
                    :multiple="false"
                    :enableCropper="true"
                    :cropToSquare="true"
                    :squareSize="256"
                    :itemWidth="190"
                    :itemHeight="190"/>
    </div>
  </div>
</template>

<script>
import settings from '@/settings.js'
import constants from '@/constants'
import formatMixins from "@/mixins/formatMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import FileUploader from "@/components/FileUploader";
import UserLanguageBadge from "@/components/UserLanguageBadge.vue";
import billingRestrictionsMixin from '@/mixins/billingRestrictionsMixin'

export default {
  emits: ['update-items'],
  mixins: [ formatMixins, showErrorsMixins, billingRestrictionsMixin ],
  components: {
    FileUploader,
    UserLanguageBadge
  },
  props: {
    user: {
      type: Object,
      default: null,
    },
    employeeIsLoading: {
      type: Boolean,
      default: false
    },
  },
	data() {
		return {
      settings,
      constants
		}
	},
  methods: {
    onAvatarUpdated({ data }) {
      if (!data?.success || !this.user?.id || !this.$store.state.user?.id || +this.user.id !== +this.$store.state.user.id) return
      this.$store.commit('SET_USER_AVATAR', data.profile_img)
    },
    updateItem() {
      this.$emit('update-items')
    },
	},
  computed: {
    avatarCurrentUrl() {
      const img = this.user?.profile_img

      if (!img) return null

      return this.settings.apiEndPoint.replace(/\/$/, '') + '/api/user/avatar?img=' + encodeURIComponent(img)
    },
  }
}
</script>

<style scoped lang="scss">
.datatable-wrapper {
  overflow-y: auto;
  padding-bottom: 25px;
}

.positions-table {
  border-collapse: collapse;
  width: 100%;
  tr {
    td {
      padding: 8px !important;
    }
  }
}
</style>